import { useEffect } from "react";
import { getVal, setVal } from "oolib";

/**
 * State for the "keep the biggest N, collapse the rest into Others" control,
 * shared by the pie chart (segments) and bar chart (bars). Both store the same
 * two props — `combineSegments` and `numberOfSegments` — so a widget switched
 * between the two types keeps the setting.
 *
 * The two charts differ only in their numbers, which is why `defaultMax` and
 * `min` are arguments: a bar chart's floor is 2, because the summarisation layer
 * reads a threshold of 0 (what a single bar produces) as "don't summarise".
 *
 * Data is passed in rather than pulled from context, matching useVisibilityModifier.
 *
 * @param {Object}   p.widgetConfig  this widget's config
 * @param {Object}   p.dataKeys      the widget's DATA_KEYS (needs combineSegments, numberOfSegments)
 * @param {Function} p.updateWidget  collab-aware config setter
 * @param {Array}    p.data          this widget's fetched datapoints
 * @param {Boolean}  p.isEnabled     whether the control applies at all (single-layer pie / non-breakdown bar)
 * @param {Number}   p.defaultMax    how many to keep when the user hasn't chosen
 * @param {Number}   p.min           stepper floor
 */
export const useCombineSegments = ({
  widgetConfig,
  dataKeys,
  updateWidget,
  data,
  isEnabled,
  defaultMax,
  min,
}) => {
  const total = data?.length || 0;
  // Floor of 1 so the stepper still has a coherent range before data arrives.
  const totalDatapoints = Math.max(1, total);

  // Combining defaults ON.
  const combine = getVal(widgetConfig, dataKeys.combineSegments) ?? true;
  const setCombine = (next) =>
    updateWidget((w) => setVal(w, dataKeys.combineSegments, next === true));

  // Default: `defaultMax` when there are more datapoints than that, else the total.
  const defaultCount = Math.max(min, Math.min(defaultMax, totalDatapoints));
  const count = getVal(widgetConfig, dataKeys.numberOfSegments) ?? defaultCount;
  const setCount = (val) =>
    updateWidget((w) => setVal(w, dataKeys.numberOfSegments, val));

  // Persist the resolved defaults into props the moment the control is usable
  // (enabled + data fetched), so the values the user SAW are what's saved —
  // otherwise an untouched default recomputes at view time against whatever the
  // data has become. Writes go through updateWidget → the collab-aware setConfig,
  // so this persists in both modes (collab: flushed live; legacy: rides Publish).
  // Idempotent: seeds only keys that are unset, so it fires once per widget/field.
  const dataLoaded = total > 0;
  useEffect(() => {
    if (!isEnabled || !dataLoaded) return;
    const seedCombine = getVal(widgetConfig, dataKeys.combineSegments) === undefined;
    const seedCount = combine && getVal(widgetConfig, dataKeys.numberOfSegments) === undefined;
    if (!seedCombine && !seedCount) return;
    updateWidget((w) => {
      if (seedCombine) w = setVal(w, dataKeys.combineSegments, true);
      if (seedCount) w = setVal(w, dataKeys.numberOfSegments, defaultCount);
      return w;
    });
  });

  return {
    combine,
    setCombine,
    count,
    setCount,
    min,
    max: Math.max(min, totalDatapoints),
  };
};
